import {
  type NamedWorkspaceAction,
  type NamedWorkspaceLocation,
  type NamedWorkspaceType,
  trackNamedWorkspaces,
} from '../../services/analytics';
import {type Viewer} from '../../state/viewer/types';
import {type LoadableView} from '../../state/views/types';
import {EMPTY_WORKSPACE_DISPLAY_NAME} from '../../state/workspaces/displayNames';
import {
  getNamedWorkspaceIdForView,
  viewIsDefault,
  viewIsLegacyWorkspace,
} from '../../state/workspaces/names';
import {
  getIsPersonalWorkspace,
  type NamedWorkspaceId,
} from '../../state/workspaces/types';
import {viewerIsWbAdminUsingPrivileges} from '../../util/admin';

export const WANDB_WORKSPACES_DOCS =
  'https://docs.wandb.ai/models/track/workspaces#saved-workspace-views';

export type WorkspaceType = 'shared' | 'personal' | 'unknown';
export function getWorkspaceType(
  namedWorkspaceId: NamedWorkspaceId,
  view?: LoadableView
): WorkspaceType {
  if (
    namedWorkspaceId == null ||
    view == null ||
    view.user == null ||
    view?.name.length === 0
  ) {
    return 'unknown';
  }

  if (viewIsLegacyWorkspace(view.name)) {
    return viewIsDefault(view.name) ? 'shared' : 'personal';
  }

  const {id} = getNamedWorkspaceIdForView(view.name, view.user);
  return getIsPersonalWorkspace(id) ? 'personal' : 'shared';
}

export function isUnnamedWorkspace(displayName: string) {
  return displayName === EMPTY_WORKSPACE_DISPLAY_NAME;
}

export function getNamedWorkspaceType(view: LoadableView): NamedWorkspaceType {
  const nwId = getNamedWorkspaceIdForView(view.name, view.user);
  let workspaceType: NamedWorkspaceType =
    nwId.nwType === 'shared workspace'
      ? 'shared'
      : nwId.nwType === 'personal workspace'
        ? 'personal'
        : 'unknown';
  if (viewIsLegacyWorkspace(view.name)) {
    workspaceType = `legacy ${workspaceType}` as NamedWorkspaceType;
  }
  return workspaceType;
}

export function parseAndTrackNamedWorkspaces({
  action,
  location,
  currentView,
  viewer,
}: {
  action: NamedWorkspaceAction;
  location: NamedWorkspaceLocation;
  currentView: LoadableView | undefined;
  viewer: Viewer | undefined;
}) {
  // skip sending analytics event if we don't have a view to work with
  if (currentView == null) {
    return;
  }

  // We re-use legacy views in the named workspaces features, so we want to
  // differentiate if a view was created with feature ON or view was just migrated.
  const nwId = getNamedWorkspaceIdForView(currentView.name, currentView.user);
  const workspaceType = getNamedWorkspaceType(currentView);

  if (action === 'View') {
    // Viewing personal and legacy personal workspaces emits too
    // many events,  and that's expensive in segment/mixpanel,
    // so we don't track them. We can use pageviews of the projects/run
    // pages as a proxy for these events.
    if (nwId.nwType === 'personal workspace') {
      return;
    }
  }

  trackNamedWorkspaces({
    action,
    location,
    viewerIsUsingAdminPowers: viewerIsWbAdminUsingPrivileges(viewer),
    viewType: currentView?.type,
    viewName: currentView?.name,
    workspaceType,
  });
}

export const DISABLED_WB_ADMIN_ACTION_TOOLTIP_CONTENT =
  'You are in admin mode. Toggle off admin mode to write to workspaces.';
const DISABLED_RESTRICTED_INSTANCE_ADMIN_TOOLTIP_CONTENT =
  'You are an instance admin viewing a restricted project and do not have write access.';

export const getDisabledTooltipContent = (viewer: Viewer | undefined) => {
  return viewerIsWbAdminUsingPrivileges(viewer)
    ? DISABLED_WB_ADMIN_ACTION_TOOLTIP_CONTENT
    : DISABLED_RESTRICTED_INSTANCE_ADMIN_TOOLTIP_CONTENT;
};

export const getResetTooltipContent = ({
  isLoadingWorkspaceTemplates,
  resetToDefaultSpecInfoIsDisabled,
  viewer,
}: {
  isLoadingWorkspaceTemplates: boolean;
  resetToDefaultSpecInfoIsDisabled: boolean;
  viewer: Viewer | undefined;
}) => {
  if (isLoadingWorkspaceTemplates) {
    return 'Loading workspace templates...';
  }
  if (resetToDefaultSpecInfoIsDisabled) {
    return getDisabledTooltipContent(viewer);
  }
  return '';
};
